import React, { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useWorker } from "../context/WorkerContext";
import ReferralPayoutCard from "../components/ReferralPayoutCard";
import referral from "../assets/referral.png";

const USE_REAL_API = true; // switched to real API

const MAX_ELIGIBLE_REFERRALS = 10;

// ─────────────────────────────────────────────────────────────────────────────
// Transform the API response into the shape ReferralPayoutCard expects
//
// API shape:
// {
//   success: true,
//   data: {
//     totalPayout: 0,
//     totalReferralReceived: 0,
//     totalReferralYet: 0,
//     currentMonth: {
//       month: "May 2026",
//       myShifts: 0,
//       referralAmount: 0,
//       eligibleWorkerCount: 0,
//       activeWorkerCount: 0,
//       referredBreakup: []           // array of referred-worker objects
//     },
//     history: []                     // array of past-month objects (same shape as currentMonth)
//   }
// }
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Build a monthKey string like "2026-5" from a month label like "May 2026".
 */
const toMonthKey = (monthStr) => {
  if (!monthStr) return "-";
  return monthStr.replace(
    /(\w+)\s(\d{4})/,
    (_, m, y) => `${y}-${new Date(`${m} 1, ${y}`).getMonth() + 1}`
  );
};

/**
 * Transform a single month block (currentMonth or a history entry).
 *
 * referredBreakup items – we assume each item may have fields like:
 *   { phone, name, currentMonthShiftCount, referAmountEarned, referralBonusReceived, ... }
 * All fields are optional; missing values fall back to "-" / 0.
 */
const transformMonthBlock = (block, isCurrentMonth = false) => {
  if (!block) return null;

  const monthStr        = block.month          ?? "-";
  const totalShifts     = block.myShifts       ?? 0;
  const referralAmount  = block.referralAmount  ?? 0;
  const paymentStatus   = block.paymentStatus  ?? null; // "success" | "failed" | "payoutGenerated" | null

  // ✅ currentMonth uses "referredBreakup", history uses "breakdown"
  const referredBreakup = block.referredBreakup ?? block.breakdown ?? [];

  // ✅ currentMonth uses "activeWorkerCount", history uses "totalWorkerCount"
  const eligibleWorkerCount = block.eligibleWorkerCount ?? 0;
const totalReferrals = block.totalWorkerCount ?? referredBreakup.length ?? 0;

  const referrals = referredBreakup.map((w) => {
    // ✅ currentMonth uses "shifts", history uses "shiftCount"
    const shiftsCompleted =
      w?.shifts     ??   // currentMonth field
      w?.shiftCount ??   // history field
      0;

    const amount = w?.referAmountEarned ?? w?.amount ?? 0;

    return {
      name:  w?.name  || "-",
      phone: w?.phone ? `+91 ${String(w.phone).replace(/^91/, "")}` : "-",
      shiftsCompleted,
      amount: isCurrentMonth ? undefined : amount,
      noPayoutMsg:
        !isCurrentMonth && shiftsCompleted < 10
          ? "*10 shifts were not completed"
          : undefined,
    };
  });

  // ── Banner messages ──────────────────────────────────────────────
  const backendMessage = block.message ?? null;
const noPayoutMessage = backendMessage;
const successMessage  = null;

  // if (isCurrentMonth) {
  //   if (totalShifts < 10) {
  //     noPayoutMessage = "Complete 10 shifts with referrals to unlock payout.";
  //   }
  // } else {
  //   const totalEarned   = referrals.reduce((s, r) => s + (r.amount || 0), 0);
  //   const eligibleCount = referrals.filter((r) => (r.amount || 0) > 0).length;

  //   if (totalShifts < 10) {
  //     noPayoutMessage =
  //       "*Your 10 shifts were not completed, so you won't get the referral amount for your referrals.";
  //   } else if (eligibleCount > 0 && totalEarned > 0) {
  //     successMessage = `Hurray! You Earned ₹${totalEarned} from ${eligibleCount} referral${eligibleCount > 1 ? "s" : ""}.`;
  //   } else if (referrals.length > 0 && eligibleCount === 0) {
  //     noPayoutMessage = `Your ${referrals.length} referral${referrals.length > 1 ? "s" : ""} didn't meet the minimum shift criteria.`;
  //   }
  // }

  return {
    monthKey: toMonthKey(monthStr),
    month: monthStr,
    isCurrentMonth,
    totalShifts,
    referralAmount,
    totalReferrals,
    eligibleWorkerCount,
    paymentStatus,  
    noPayoutMessage,
    successMessage,
    referrals,
  };
};

/**
 * Transform the full API data object into:
 *   { payoutData, totalPayout, totalEligible }
 */
const transformApiResponse = (data) => {
  if (!data) return { payoutData: [], totalPayout: 0, totalEligible: 0, totalReferrals: 0 };


  const currentMonthBlock = transformMonthBlock(data.currentMonth, true);
  const historyBlocks     = (data.history ?? []).map((h) => transformMonthBlock(h, false));

  // Deduplicate: if history somehow contains the current month, skip it
  const currentMonthKey = currentMonthBlock?.monthKey;
  const filteredHistory = historyBlocks.filter((h) => h && h.monthKey !== currentMonthKey);

  const payoutData = [
    ...(currentMonthBlock ? [currentMonthBlock] : []),
    ...filteredHistory,
  ];

  // Use top-level totals when available, fall back to derived values
  const totalPayout    = data.totalPayout           ?? 0;
  const totalEligible = data.totalReferralReceived ?? 0;
  const totalReferrals = data.totalReferrals         ?? 0;

  return { payoutData, totalPayout, totalEligible, totalReferrals };
};

// ─────────────────────────────────────────────────────────────────────────────
// Page Component
// ─────────────────────────────────────────────────────────────────────────────
const ReferralPayoutPage = () => {
  const navigate = useNavigate();
  const { user, fetchMonthlyPayout } = useWorker();

  const [payoutData,      setPayoutData]      = useState([]);
  const [totalPayout,     setTotalPayout]     = useState(0);
  const [totalEligible,   setTotalEligible]   = useState(0);
  const [expandedMonths,  setExpandedMonths]  = useState({});
  const [apiLoading,      setApiLoading]      = useState(true);
  const [apiError,        setApiError]        = useState(null);
  const [totalReferrals, setTotalReferrals] = useState(0);

  // ── Load data ────────────────────────────────────────────────────
  useEffect(() => {
    if (!USE_REAL_API) return;

    const load = async () => {
      if (!user?.id) return;
      setApiLoading(true);
      setApiError(null);

      try {
        const res = await fetchMonthlyPayout(user.id);

        if (res?.success && res?.data) {
          const { payoutData: pd, totalPayout: tp, totalEligible: te, totalReferrals: tr } =
  transformApiResponse(res.data);
  setTotalReferrals(tr);

          setPayoutData(pd);
          setTotalPayout(tp);
          setTotalEligible(te);

          // Auto-expand the first (current) month
          if (pd.length > 0) {
            setExpandedMonths({ [pd[0].monthKey]: true });
          }
        } else {
          setPayoutData([]);
        }
      } catch (err) {
        console.error("Failed to load payout data:", err);
        setApiError("Failed to load payout data. Please try again.");
      } finally {
        setApiLoading(false);
      }
    };

    load();
  }, [user?.id]);

  // ── Toggle ───────────────────────────────────────────────────────
  const toggleMonth = useCallback((monthKey) => {
    setExpandedMonths((prev) => ({ ...prev, [monthKey]: !prev[monthKey] }));
  }, []);

  // ── Render ───────────────────────────────────────────────────────
  return (
    <div
      style={{
        minHeight: "100vh",
        background: "#f0f0f5",
        fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
        paddingTop: "90px",
      }}
    >
      <div style={{ maxWidth: 480, margin: "0 auto" }}>

        {/* Header */}
        <div className="relative flex items-center gap-4 justify-center mb-6">
          <button onClick={() => navigate(-1)} className="text-2xl absolute left-6">
            ‹
          </button>
          <h2 className="text-xl font-bold text-center">Referral Payout</h2>
        </div>

        {/* Summary Banner */}
        <div
          style={{
            display: "flex",
            alignItems: "flex-start",
            justifyContent: "space-between",
            padding: "4px 20px 20px",
          }}
        >
          <div>
            <div style={{ fontSize: 12, color: "#666", marginBottom: 2 }}>
              Total Payout
            </div>
            <div
              style={{
                fontSize: 25,
                fontWeight: 800,
                color: "#08912A",
                lineHeight: 1.1,
                marginBottom: 10,
              }}
            >
              {apiLoading ? "-" : `₹${totalPayout}.00`}
            </div>
            <div style={{ fontSize: 11.5, color: "#555" }}>
  Total Eligible Referrals :{" "}
  <span style={{ color: "#22c55e", fontWeight: 700, fontSize: 16 }}>
    {apiLoading ? "-" : totalEligible}
  </span>
  <span style={{ color: "#000", fontWeight: 700, paddingLeft: 4, fontSize: 12 }}>
    / {apiLoading ? "-" : totalReferrals}  
  </span>
</div>
          </div>
          <img
            src={referral}
            alt="Referral Illustration"
            style={{ height: 100, margin: "0px 50px" }}
          />
        </div>

        {/* Content */}
        {apiLoading ? (
          <div style={{ textAlign: "center", padding: "40px 20px", color: "#888", fontSize: 14 }}>
            Loading payout data…
          </div>
        ) : apiError ? (
          <div
            style={{
              margin: "0 12px",
              padding: "16px",
              background: "#fff0f0",
              borderRadius: 12,
              color: "#c0392b",
              fontSize: 13,
              textAlign: "center",
            }}
          >
            {apiError}
          </div>
        ) : payoutData.length === 0 ? (
          <div style={{ textAlign: "center", padding: "40px 20px", color: "#aaa", fontSize: 14 }}>
            No payout data available.
          </div>
        ) : (
          <div
            style={{
              padding: "0 12px",
              display: "flex",
              flexDirection: "column",
              gap: 12,
              paddingBottom: 32,
            }}
          >
            {payoutData.map((monthData) => (
              <ReferralPayoutCard
                key={monthData.monthKey}
                month={monthData.month}
                isCurrentMonth={monthData.isCurrentMonth}
                totalShifts={monthData.totalShifts}
                referralAmount={monthData.referralAmount}
                eligibleReferralsCount={monthData.eligibleWorkerCount}
                totalReferrals={monthData.totalReferrals}
                referrals={monthData.referrals}
                noPayoutMessage={monthData.noPayoutMessage}
                successMessage={monthData.successMessage}
                isExpanded={!!expandedMonths[monthData.monthKey]}
                onToggle={() => toggleMonth(monthData.monthKey)}
                currentEligibleCount={totalReferrals}
              />
            ))}
          </div>
        )}

      </div>
    </div>
  );
};

export default ReferralPayoutPage;